import { ActionIcon, Box, Button, Divider, Group, Loader, Stack, Switch, Text, TextInput, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconDownload, IconMailFast, IconPencil, IconPlus, IconTrash, IconX } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useBlocker, useLocation, useMatchRoute, useNavigate } from "@tanstack/react-router";
import { Suspense, useEffect, useState } from "react";
import { GroupParentsRepository } from "shared/components/organization/groups/data";
import { GroupSelector } from "shared/components/organization/groups/groups";
import { MemberDetails } from "shared/components/organization/groups/MemberDetails";
import { useArchiveGroup } from "shared/components/organization/groups/useArchiveGroup";
import { SendersContainer } from "shared/components/organization/senders/SendersContainer";
import { groupsQueryOptions, useAddParentToGroup, useRemoveParentFromGroup, useUpdateGroup } from "shared/graphql/groups";
import { permissionCheck, usePermissionCheck, usePermissions } from "shared/stores/oidc";
import { useAside } from "shared/utils/aside";
import { logger } from "shared/utils/logger";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { GroupLikeFragment, Permission } from "src/gql/graphql";
import { queryClient } from "src/queryClient";
import { container } from "tsyringe";

const GroupDetailsContainer = ({ groupId }: { groupId: string }) => {
    const nav = useNavigate();
    const { permissions } = usePermissions();
    const canViewMembership = usePermissionCheck(Permission.GroupUsersView, groupId);
    const canSendToGroup = usePermissionCheck(Permission.GroupMessageSendTo, groupId);
    const hasSenderManage = usePermissionCheck(Permission.GroupSenderManage, groupId);

    const {
        data: { rootGroup },
    } = useSuspenseQuery(groupsQueryOptions.rootGroup);

    const {
        data: { group },
    } = useSuspenseQuery(groupsQueryOptions.group(groupId));

    const repo = container.resolve(GroupParentsRepository);
    const loader = repo.get({ childId: groupId, queryMembership: canViewMembership });
    const parents = loader.useItems();

    const form = useForm<GroupLikeFragment & { parents: GroupLikeFragment[] }>({
        mode: "controlled",
        initialValues: {
            ...group,
            parents,
        },
        validate: {
            name: (v) => (v.length == 0 ? "Invalid name" : null),
            parents: (v) => (group.id != rootGroup.id && v.length == 0 ? "Group must have at least one parent" : null),
        },
    });

    const [parentSelectorOpened, { toggle: toggleParentSelector }] = useDisclosure();
    const [isEditingName, { open: openNameEditor, close: closeNameEditor }] = useDisclosure(false);
    const [isUpdatingName, setIsUpdatingName] = useState(false);
    const [isUpdatingJoinable, setIsUpdatingJoinable] = useState(false);

    const { addParentAsync } = useAddParentToGroup((parentId) =>
        form.setValues({ parents: form.getValues().parents.filter((it) => it.id != parentId) }),
    );
    const { updateGroupAsync } = useUpdateGroup();
    const { removeParentAsync } = useRemoveParentFromGroup();
    const { archiveGroup } = useArchiveGroup(group);

    async function handleNameSave() {
        const validation = form.validateField("name");
        if (validation.hasError) return;
        if (!form.isDirty("name")) {
            closeNameEditor();
            return;
        }

        const { id, name, internalJoinable } = form.getValues();

        setIsUpdatingName(true);

        try {
            await updateGroupAsync({ internalJoinable, name, groupId: id });
            await Promise.all([qk.invalidate("group"), qk.invalidate("user")]);
            notify.show.success("Updated group name");
            form.setInitialValues({ ...form.getInitialValues(), name });
            form.setDirty((prev) => ({ ...prev, name: false }));
            closeNameEditor();
        } catch (error) {
            notify.catch(error);
        } finally {
            setIsUpdatingName(false);
        }
    }

    async function handleJoinableSwitchChange(next: boolean) {
        if (group.synchronized || isUpdatingJoinable) return;
        const previous = form.getInitialValues().internalJoinable;
        if (next === previous) return;
        const { name } = form.getValues();

        setIsUpdatingJoinable(true);

        try {
            await updateGroupAsync({ internalJoinable: next, name, groupId: group.id });
            await Promise.all([qk.invalidate("group"), qk.invalidate("user")]);
            notify.show.success(next ? "Group is now joinable internally" : "Group joinable disabled");
            form.setInitialValues({ ...form.getInitialValues(), internalJoinable: next });
            form.setDirty((prev) => ({ ...prev, internalJoinable: false }));
        } catch (error) {
            notify.catch(error);
            form.setFieldValue("internalJoinable", previous);
            form.setDirty((prev) => ({ ...prev, internalJoinable: false }));
        } finally {
            setIsUpdatingJoinable(false);
        }
    }

    function handleStartNameEdit() {
        form.setFieldValue("name", form.getInitialValues().name);
        openNameEditor();
    }

    function handleCancelNameEdit() {
        form.setFieldValue("name", form.getInitialValues().name);
        form.setDirty((prev) => ({ ...prev, name: false }));
        closeNameEditor();
    }

    function handleSaveParents() {
        const newParents = form.getValues().parents.filter((p) => !parents.some((g) => g.id == p.id));
        const removedParents = parents.filter((p) => !form.getValues().parents.some((g) => g.id == p.id));
        Promise.all(newParents.map((p) => addParentAsync({ groupId: group.id, parentId: p.id })))
            .then(() => Promise.all(removedParents.map((p) => removeParentAsync({ groupId: group.id, parentId: p.id }))))
            .then(async () => {
                await queryClient.invalidateQueries({ queryKey: ["group"] });
                await loader.refetch();
                notify.show.success("Updated group parents");
                form.setInitialValues({ ...group, parents: form.getValues().parents });
                form.reset();
                toggleParentSelector();
            })
            .catch(logger.error);
    }

    function handleRemoveParent(parentId: string) {
        removeParentAsync({ groupId: group.id, parentId })
            .then(async () => {
                await qk.invalidate("group");
                await loader.refetch();
                notify.show.success("Removed parent group");
                form.setInitialValues({ ...group, parents: form.getValues().parents.filter((it) => it.id != parentId) });
                form.reset();
            })
            .catch(notify.catch);
    }

    useEffect(() => {
        const values = form.getValues();
        if (values.id != group.id && parents.length != values.parents.length) {
            form.setInitialValues({ ...group, parents });
            closeNameEditor();
            form.reset();
        }
    }, [group, parents]);

    useBlocker({
        blockerFn: () => {
            if (window.confirm("This action will lose currently unsaved changes. Continue anyways?")) {
                form.reset();
                return false;
            }
            return true;
        },
        condition: form.isDirty(),
    });

    const nameInputKey = form.key("name");
    const nameLabelId = `${nameInputKey}-label`;

    return (
        <Stack style={{ overflowY: "auto" }}>
            <form
                onSubmit={(event) => {
                    event.preventDefault();
                    void handleNameSave();
                }}
            >
                <Stack>
                    <Stack gap="xs">
                        <Text id={nameLabelId} fw={600} fz="sm">
                            Group Name
                        </Text>
                        {isEditingName ? (
                            <Group wrap="nowrap" align="center" gap="xs">
                                <TextInput
                                    key={nameInputKey}
                                    placeholder="Enter name"
                                    autoFocus
                                    flex={1}
                                    aria-labelledby={nameLabelId}
                                    {...form.getInputProps("name")}
                                />
                                <ActionIcon
                                    type="submit"
                                    variant="subtle"
                                    disabled={!form.isDirty("name") || isUpdatingName}
                                    aria-label="Save group name"
                                >
                                    {isUpdatingName ? <Loader size="xs" /> : <IconDownload size={16} />}
                                </ActionIcon>
                                <ActionIcon
                                    variant="transparent"
                                    onClick={handleCancelNameEdit}
                                    aria-label="Cancel editing group name"
                                    disabled={isUpdatingName}
                                >
                                    <IconX size={16} />
                                </ActionIcon>
                            </Group>
                        ) : (
                            <Group align="center" wrap="nowrap">
                                <Text>{form.values.name}</Text>
                                <ActionIcon
                                    component="button"
                                    variant="transparent"
                                    onClick={handleStartNameEdit}
                                    aria-label="Edit group name"
                                >
                                    <IconPencil size={16} />
                                </ActionIcon>
                            </Group>
                        )}
                    </Stack>
                    <Group align="center" gap="xs">
                        <Switch
                            key={form.key("internalJoinable")}
                            label="Joinable"
                            w="fit-content"
                            py="sm"
                            size="sm"
                            checked={form.values.internalJoinable}
                            onChange={(event) => {
                                const next = event.currentTarget.checked;
                                form.setFieldValue("internalJoinable", next);
                                void handleJoinableSwitchChange(next);
                            }}
                            disabled={group.synchronized || isUpdatingJoinable}
                        />
                        {isUpdatingJoinable && <Loader size="xs" />}
                    </Group>
                    {canSendToGroup && (
                        <Button
                            variant="default"
                            rightSection={<IconMailFast />}
                            onClick={() => void nav({ to: "/messages/compose", search: { groupId: group.id } })}
                        >
                            Send Message to Group
                        </Button>
                    )}
                    <Divider />
                    {hasSenderManage && (
                        <Stack>
                            <SendersContainer groupId={group.id} />
                            <Divider />
                        </Stack>
                    )}
                    {parentSelectorOpened ? (
                        <Stack>
                            <Group>
                                <Title order={3}>Parent(s)</Title>
                                <Button variant="default" leftSection={<IconDownload size={16} />} onClick={handleSaveParents}>
                                    Save
                                </Button>
                                <Button
                                    variant="default"
                                    leftSection={<IconDownload size={16} />}
                                    onClick={() => {
                                        form.reset();
                                        toggleParentSelector();
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Group>
                            <GroupSelector
                                selectedGroups={form.getValues().parents.map((it) => ({ ...it, parents: [], children: [] }))}
                                onChange={(group, checked) =>
                                    form.setValues({
                                        parents: checked
                                            ? form.getValues().parents.concat(group)
                                            : form.getValues().parents.filter((it) => it.id != group.id),
                                    })
                                }
                                shouldDisable={(g) => {
                                    const canManage = permissionCheck(permissions, Permission.GroupSubgroupManage, g.id);
                                    return !canManage || g.id == group.id || g.synchronized;
                                }}
                            />
                        </Stack>
                    ) : (
                        <Stack>
                            <Group>
                                <Title order={3}>Parent(s)</Title>
                                <Button
                                    variant="default"
                                    leftSection={<IconPlus size={16} />}
                                    onClick={toggleParentSelector}
                                    disabled={group.id == rootGroup.id}
                                >
                                    Add
                                </Button>
                            </Group>
                            {form.getValues().parents.map((parent) => (
                                <Group key={parent.id} justify="space-between">
                                    <Text>{parent.name}</Text>
                                    <ActionIcon
                                        variant="transparent"
                                        onClick={() => handleRemoveParent(parent.id)}
                                        aria-label="Remove parent"
                                    >
                                        <IconX size={16} />
                                    </ActionIcon>
                                </Group>
                            ))}
                            <loader.LoadTrigger />
                        </Stack>
                    )}
                    <Divider />
                </Stack>
            </form>
            {canViewMembership && (
                <>
                    <MemberDetails groupId={group.id} />
                    <Divider />
                </>
            )}
            <Box>
                <Button leftSection={<IconTrash size={16} />} onClick={() => void archiveGroup()}>
                    Archive
                </Button>
            </Box>
        </Stack>
    );
};

function Page() {
    const aside = useAside();
    const nav = Route.useNavigate();
    const { href } = useLocation();
    const { groupId } = Route.useParams();
    const matchRoute = useMatchRoute();

    useEffect(() => {
        if (!matchRoute({ to: "/organization/groups" })) aside.dismiss({ silent: true });
    }, [href]);

    useEffect(() => {
        aside.present({
            sticky: true,
            label: "Edit Group",
            component: (
                <Suspense fallback={<Loader />}>
                    <GroupDetailsContainer key={groupId} groupId={groupId} />
                </Suspense>
            ),
            didDismiss: () => nav({ to: "/organization/groups", search: (prev) => ({ ...prev }) }),
        });
    }, [groupId]);

    return null;
}

/**
 * @public
 */
export const Route = createFileRoute("/_auth/organization/groups/$groupId")({
    loader: ({ params: { groupId } }) => {
        queryClient.ensureQueryData(groupsQueryOptions.rootGroup).catch(logger.error);
        queryClient.ensureQueryData(groupsQueryOptions.group(groupId)).catch(logger.error);
    },
    component: () => (
        <Suspense fallback={<Loader />}>
            <Page />
        </Suspense>
    ),
});
